/**
 * RestoID — the consent screen's decision row.
 * ---------------------------------------------------------------------------
 * Replaces the pair of upstream `@/shared/components/Button`s in
 * `src/pages/Consent/index.tsx` (`action.cancel` as `type="secondary"` +
 * `action.authorize` as the primary).
 *
 * The RestoID primitive set has no outlined button — `GhostAction` is the
 * secondary, but it is a small centred pill and would read as a footnote next to
 * a 54px CTA. So the deny action is a GhostAction-shaped button at CTA geometry
 * (same height, same radius, same label weight, ghost fill and hover), which is
 * what the design reference's ConsentScreen draws: two equal buttons, deny quiet,
 * authorize lime.
 *
 * Upstream behaviour kept: the deny button exists ONLY when the consent info
 * carried a redirect URI (there is nowhere to send the user otherwise), and the
 * authorize button carries the submit's loading state. With no deny, authorize
 * takes the full width by itself.
 */
import { type ReactNode, useState } from 'react';

import { Button, C, FONT, SIZE } from './restoid';

const DenyButton = ({ title, onClick }: { title: ReactNode; onClick: () => void }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      type="button"
      style={{
        width: '100%',
        height: SIZE.cta,
        borderRadius: 12,
        cursor: 'pointer',
        background: isHovered ? C.cardHover : 'transparent',
        border: `1px solid ${isHovered ? C.line : C.lineSoft}`,
        color: isHovered ? C.text : C.sub,
        font: `600 15.5px/1 ${FONT}`,
        letterSpacing: '0.01em',
        transition: 'background .15s ease, border-color .15s ease, color .15s ease',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      {title}
    </button>
  );
};

type Props = {
  denyLabel: ReactNode;
  /** Absent when the consent info carried no redirect URI — then no deny button. */
  onDeny?: () => void;
  authorizeLabel: ReactNode;
  isAuthorizing: boolean;
  onAuthorize: () => void;
};

export const ConsentActions = ({
  denyLabel,
  onDeny,
  authorizeLabel,
  isAuthorizing,
  onAuthorize,
}: Props) => (
  <div style={{ display: 'flex', gap: 12 }}>
    {onDeny ? (
      <div style={{ flex: 1, minWidth: 0 }}>
        <DenyButton title={denyLabel} onClick={onDeny} />
      </div>
    ) : null}
    <div style={{ flex: 1, minWidth: 0 }}>
      <Button title={authorizeLabel} isLoading={isAuthorizing} onClick={onAuthorize} />
    </div>
  </div>
);
